import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Article as ArticleIcon,
  Bolt as BoltIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  OpenInNew as OpenIcon,
  Visibility as VisibilityIcon,
  Workspaces as WorkspacesIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useSWR, { mutate as globalMutate } from 'swr';
import { Controller, useForm, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { tenants, type TenantCreateRequest } from '@/api/platform';
import { request } from '@/api/client';
import { DataTable } from '@/components/DataTable';
import { FieldRow } from '@/components/FieldRow';
import { MetricStat } from '@/components/MetricStat';
import { StatusPill } from '@/components/StatusPill';
import { TenantTypeSelect } from '@/components/TenantTypeSelect';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime, utcStamp } from '@/utils/format';
import type { Tenant } from '@/types/backend';
import { TenantApiAccessDialog } from './TenantApiAccessDialog';

/**
 * Canonical tenant list, backed by /admin/tenants. The previous version
 * derived a tenant set from facilities + security + webhooks because no
 * tenant-master endpoint existed — that workaround is gone now that
 * {@code TenantAdminController} ships in the backend.
 *
 * Status vocabulary follows the data-as-it-exists convention used by the
 * already-onboarded tenants: "Pending", "Approved", "Suspended".
 */

const STATUS_CHOICES = ['Pending', 'Approved', 'Suspended'] as const;

const statusTone = (status: string | undefined): 'success' | 'warning' | 'danger' | 'mono' => {
  switch (status) {
    case 'Approved':  return 'success';
    case 'Pending':   return 'warning';
    case 'Suspended': return 'danger';
    default:          return 'mono';
  }
};

export function TenantsPage() {
  const theme = useTheme();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [viewing, setViewing] = useState<Tenant | null>(null);
  const [apiAccessFor, setApiAccessFor] = useState<Tenant | null>(null);
  /** Track where Edit / API access were launched from so the Back button
   *  knows whether to return to the View dialog or just close. */
  const [editFrom, setEditFrom] = useState<'view' | 'list' | null>(null);
  const [apiAccessFrom, setApiAccessFrom] = useState<'view' | 'list' | null>(null);

  const { data, error, isLoading } = useSWR(
    '/admin/tenants',
    () => tenants.list(),
  );
  const refresh = () => globalMutate('/admin/tenants');

  const totalCount = data?.length ?? 0;
  const approved = data?.filter((t) => t.status === 'Approved').length ?? 0;
  const pending = data?.filter((t) => t.status === 'Pending').length ?? 0;

  return (
    <PageShell
      kicker="PLATFORM · TENANTS"
      title="Tenants"
      subtitle="Provision new tenants and inspect existing ones. Each tenant binds to facilities, security policies, and webhook subscriptions on the other admin pages."
      width="wide"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          New tenant
        </Button>
      }
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2.5, mb: 3 }}>
        <MetricStat label="Total tenants" value={isLoading ? '…' : totalCount} />
        <MetricStat label="Approved" value={isLoading ? '…' : approved} tone="success" hint="live tenants" />
        <MetricStat label="Pending"  value={isLoading ? '…' : pending}  tone={pending > 0 ? 'warning' : 'success'} hint="awaiting credentials" />
      </Box>

      <DataTable<Tenant>
        rows={data}
        rowKey={(r) => r.tenantId}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        searchable
        searchPlaceholder="Search organization, contact, tenant id…"
        paginated
        empty={{
          icon: WorkspacesIcon,
          title: 'No tenants yet.',
          description: 'Provision the first tenant to start onboarding clinics into ABDM.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New tenant</Button>,
        }}
        onRowClick={(r) => setViewing(r)}
        columns={[
          {
            key: 'organization',
            header: 'Organization',
            sortable: true,
            sortValue: (r) => r.organizationName,
            searchValue: (r) => `${r.organizationName} ${r.tenantId}`,
            pinned: true,
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.organizationName}</Typography>
                <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: theme.typography.fontFamily }}>
                  {redactMiddle(r.tenantId, 8, 8)}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'type',
            header: 'Type',
            sortable: true,
            sortValue: (r) => r.organizationType ?? '',
            filter: { type: 'select', getFilterValue: (r) => r.organizationType ?? '' },
            cell: (r) => <Typography variant="caption" color="text.secondary">{r.organizationType ?? '—'}</Typography>,
            width: 130,
          },
          {
            key: 'contact',
            header: 'Contact',
            sortable: true,
            sortValue: (r) => r.contactEmail ?? '',
            searchValue: (r) => `${r.contactEmail ?? ''} ${r.contactPerson ?? ''}`,
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="caption" color="text.secondary">{r.contactEmail ?? '—'}</Typography>
                {r.contactPerson && <Typography variant="caption" color="text.tertiary">{r.contactPerson}</Typography>}
              </Stack>
            ),
            width: 220,
          },
          {
            key: 'status',
            header: 'Status',
            sortable: true,
            sortValue: (r) => r.status ?? '',
            filter: { type: 'select', getFilterValue: (r) => r.status ?? '' },
            cell: (r) => <StatusPill tone={statusTone(r.status)}>{r.status ?? '—'}</StatusPill>,
            width: 110,
          },
          {
            key: 'env',
            header: 'Env',
            sortable: true,
            sortValue: (r) => r.environment ?? '',
            filter: { type: 'select', getFilterValue: (r) => r.environment ?? '' },
            cell: (r) => (
              <StatusPill tone={r.environment === 'PRODUCTION' ? 'success' : 'info'}>
                {r.environment ?? 'SANDBOX'}
              </StatusPill>
            ),
            width: 120,
          },
          {
            key: 'mode',
            header: 'Mode',
            sortable: true,
            sortValue: (r) => r.integrationMode ?? '',
            filter: { type: 'select', getFilterValue: (r) => r.integrationMode ?? 'PLATFORM' },
            cell: (r) => (
              <Typography variant="caption" color="text.secondary">{r.integrationMode ?? 'PLATFORM'}</Typography>
            ),
            width: 110,
          },
          {
            key: 'updated',
            header: 'Updated',
            sortable: true,
            sortValue: (r) => r.updatedAt ?? '',
            cell: (r) => <Typography variant="caption" color="text.secondary">{relativeTime(r.updatedAt)}</Typography>,
            width: 130,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: 360,
            pinned: true,
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="View tenant">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setViewing(r); }}
                    aria-label="view tenant"
                  >
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit tenant">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setEditFrom('list'); setEditing(r); }}
                    aria-label="edit tenant"
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="API access / subscription">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setApiAccessFrom('list'); setApiAccessFor(r); }}
                    aria-label="manage api access"
                  >
                    <BoltIcon sx={{ fontSize: 18, color: theme.palette.brand.main }} />
                  </IconButton>
                </Tooltip>
                <Button
                  component={RouterLink}
                  to={`/platform/audit?tenantId=${encodeURIComponent(r.tenantId)}`}
                  size="small"
                  variant="text"
                  startIcon={<ArticleIcon sx={{ fontSize: 16 }} />}
                  sx={{ fontSize: 12 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Audit
                </Button>
                <Button
                  component={RouterLink}
                  to={`/platform/security?tenantId=${encodeURIComponent(r.tenantId)}`}
                  size="small"
                  variant="text"
                  startIcon={<OpenIcon sx={{ fontSize: 16 }} />}
                  sx={{ fontSize: 12 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Policy
                </Button>
              </Stack>
            ),
          },
        ]}
      />

      <TenantFormDialog
        open={creating || !!editing}
        initial={editing}
        onClose={() => { setCreating(false); setEditing(null); setEditFrom(null); }}
        onBack={editFrom === 'view' ? () => {
          // Pop back to the View dialog with the same tenant in focus.
          const t = editing;
          setEditing(null);
          setEditFrom(null);
          setViewing(t);
        } : undefined}
        onSaved={(updated) => {
          setCreating(false);
          setEditing(null);
          // If the operator drilled in from View, hop back to View with the
          // freshly-saved row so they can see the change before closing.
          if (editFrom === 'view' && updated) setViewing(updated);
          setEditFrom(null);
          refresh();
        }}
      />

      <TenantViewDialog
        tenant={viewing}
        onClose={() => setViewing(null)}
        onEdit={() => { setEditFrom('view'); setEditing(viewing); setViewing(null); }}
        onApiAccess={() => { setApiAccessFrom('view'); setApiAccessFor(viewing); setViewing(null); }}
      />

      <TenantApiAccessDialog
        tenant={apiAccessFor}
        open={!!apiAccessFor}
        onClose={() => { setApiAccessFor(null); setApiAccessFrom(null); }}
        onBack={apiAccessFrom === 'view' ? () => {
          const t = apiAccessFor;
          setApiAccessFor(null);
          setApiAccessFrom(null);
          setViewing(t);
        } : undefined}
      />
    </PageShell>
  );
}

// -------------------- Form dialog (create + edit) --------------------

interface TenantForm {
  tenantId: string;
  organizationName: string;
  organizationType: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  hfrId: string;
  callbackUrl: string;
  abhaEnabled: boolean;
  status: string;
  username: string;
  password: string;
  role: string;
  userFullName: string;
  userEmail: string;
  userMobile: string;
  // ABDM identity (HIP / CM / HIU ids — used on outbound ABDM calls)
  abdmHIPID: string;
  abdmXCMID: string;
  abdmXHIUID: string;
  // Platform-issued tenant credentials. Auto-generated server-side on
  // create; rotated via the Regenerate button. Read-only in the form.
  clientId: string;
  clientSecret: string;
  // FRD additions
  /** "" (top-level / consumer-org tenant) or a parent tenantId for child tenants. */
  parentTenantId: string;
  gstin: string;
  environment: 'SANDBOX' | 'PRODUCTION';
  plan: 'STARTER' | 'PROVIDER' | 'NETWORK' | 'ENTERPRISE';
  /** BRD §4 integration mode. */
  integrationMode: 'PLATFORM' | 'ENTERPRISE' | 'SDK';
}

/**
 * FRD §8.0.3 deployment scenarios. The choice picks the right defaults for
 * the rest of the form and decides whether the new tenant is a top-level
 * "consumer org" or a child of an existing one.
 */
type Scenario = 'V1_VENDOR' | 'V2_CHAIN_HQ' | 'V2_CHAIN_BRANCH' | 'V3_CLINIC';

const SCENARIO_LABELS: Record<Scenario, string> = {
  V1_VENDOR:        'V1 — HMIS Vendor (top-level, manages many hospital tenants)',
  V2_CHAIN_HQ:      'V2 — Hospital Chain HQ (top-level, manages many branches)',
  V2_CHAIN_BRANCH:  'V2 — Branch under an existing Chain HQ',
  V3_CLINIC:        'V3 — Standalone Clinic (1 tenant, no hierarchy)',
};

function TenantFormDialog({
  open,
  initial,
  onClose,
  onSaved,
  onBack,
}: {
  open: boolean;
  initial: Tenant | null;
  onClose: () => void;
  /** Receives the freshly saved tenant so callers can hop back to a refreshed view. */
  onSaved: (saved?: Tenant) => void;
  /** When set, replaces the "Cancel" button with a "Back" button that returns to the previous dialog. */
  onBack?: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const isEdit = !!initial;

  const { control, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm<TenantForm>({
    defaultValues: blankForm(),
  });

  // Scenario chooser drives parent-tenant + plan defaults. The choice is
  // editor-state only — it isn't persisted; what gets saved is the
  // resulting parentTenantId / plan / organizationType combination.
  const [scenario, setScenario] = useState<Scenario>('V1_VENDOR');

  useEffect(() => {
    if (open) {
      reset(formFromTenant(initial));
      // Infer the scenario from existing data when editing.
      if (initial?.parentTenantId) setScenario('V2_CHAIN_BRANCH');
      else if (initial?.organizationType === 'SOFTWARE_COMPANY') setScenario('V1_VENDOR');
      else if (initial?.organizationType === 'CLINIC' || initial?.organizationType === 'OWN') setScenario('V3_CLINIC');
      else setScenario('V2_CHAIN_HQ');
    }
  }, [open, initial, reset]);

  // Apply scenario defaults whenever the user picks a different branch.
  useEffect(() => {
    if (!open || isEdit) return; // don't stomp existing tenant on edit
    switch (scenario) {
      case 'V1_VENDOR':
        setValue('organizationType', 'SOFTWARE_COMPANY');
        setValue('parentTenantId', '');
        setValue('plan', 'PROVIDER');
        break;
      case 'V2_CHAIN_HQ':
        setValue('organizationType', 'HOSPITAL');
        setValue('parentTenantId', '');
        setValue('plan', 'NETWORK');
        break;
      case 'V2_CHAIN_BRANCH':
        setValue('organizationType', 'HOSPITAL');
        setValue('plan', 'NETWORK');
        // parentTenantId chosen explicitly via the picker below
        break;
      case 'V3_CLINIC':
        setValue('organizationType', 'CLINIC');
        setValue('parentTenantId', '');
        setValue('plan', 'STARTER');
        break;
    }
  }, [scenario, open, isEdit, setValue]);

  const watchedParent = watch('parentTenantId');

  const onSubmit = async (form: TenantForm) => {
    try {
      const body: TenantCreateRequest = {
        tenantId: form.tenantId.trim() || undefined,
        organizationName: form.organizationName.trim(),
        organizationType: form.organizationType.trim() || undefined,
        contactPerson: form.contactPerson.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        hfrId: form.hfrId.trim() || undefined,
        callbackUrl: form.callbackUrl.trim() || undefined,
        abhaEnabled: form.abhaEnabled,
        status: form.status,
        username: form.username.trim() || undefined,
        // Send password ONLY when filled — keeps existing value on edit
        // when the operator leaves the field blank.
        ...(form.password ? { password: form.password } : {}),
        role: form.role.trim() || undefined,
        userFullName: form.userFullName.trim() || undefined,
        userEmail: form.userEmail.trim() || undefined,
        userMobile: form.userMobile.trim() || undefined,
        address: form.address.trim() || undefined,
        abdmHIPID: form.abdmHIPID.trim() || undefined,
        abdmXCMID: form.abdmXCMID.trim() || undefined,
        abdmXHIUID: form.abdmXHIUID.trim() || undefined,
        // clientId / clientSecret are server-generated; never written from the form.
        // FRD additions
        parentTenantId: form.parentTenantId.trim() || undefined,
        gstin: form.gstin.trim() || undefined,
        environment: form.environment,
        plan: form.plan,
        integrationMode: form.integrationMode,
      };
      let saved: Tenant;
      if (isEdit && initial) {
        saved = await tenants.update(initial.tenantId, body);
        toast.success('Tenant updated', initial.organizationName);
      } else {
        saved = await tenants.create(body);
        toast.success('Tenant created', `${saved.organizationName} · ${saved.tenantId.slice(0, 8)}…`);
      }
      onSaved(saved);
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Save failed', detail?.message ?? detail?.error ?? (e as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {isEdit ? `Edit ${initial!.organizationName}` : 'New tenant'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="tenant-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {!isEdit && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: theme.radius.sm + 'px',
                  border: `1px solid ${theme.palette.brand.main}33`,
                  bgcolor: theme.palette.surface.sunken,
                }}
              >
                <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>
                  Onboarding scenario (FRD §8.0.3)
                </Typography>
                <TextField
                  label="What best describes this tenant?"
                  select
                  fullWidth
                  size="small"
                  value={scenario}
                  onChange={(e) => setScenario(e.target.value as Scenario)}
                  helperText="The scenario picks defaults for type and plan; you can still edit each field below."
                >
                  {(Object.keys(SCENARIO_LABELS) as Scenario[]).map((s) => (
                    <MenuItem key={s} value={s}>{SCENARIO_LABELS[s]}</MenuItem>
                  ))}
                </TextField>
                {scenario === 'V2_CHAIN_BRANCH' && (
                  <Box sx={{ mt: 1.5 }}>
                    <Controller
                      name="parentTenantId"
                      control={control}
                      rules={scenario === 'V2_CHAIN_BRANCH' ? { required: 'pick the parent (chain HQ / vendor) tenant' } : {}}
                      render={({ field, fieldState }) => (
                        <ParentTenantPicker
                          value={field.value}
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </Box>
                )}
              </Box>
            )}
            {!isEdit && (
              <Controller
                name="tenantId"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Tenant ID (optional)" fullWidth helperText="Leave blank to auto-generate a UUID. Use a stable id when migrating from another system." />
                )}
              />
            )}
            <Controller
              name="organizationName"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Organization name" required fullWidth error={!!fieldState.error} helperText={fieldState.error?.message ?? ' '} />
              )}
            />
            {/* FRD §7.1 — GSTIN, environment, plan. */}
            <Controller
              name="gstin"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="GSTIN" fullWidth helperText="15-character tax id, when applicable." inputProps={{ maxLength: 15, style: { textTransform: 'uppercase' } }} />
              )}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Controller
                name="environment"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Environment" select fullWidth helperText="Sandbox vs production gateway. Per-tenant — one tenant can be live while another is still in sandbox.">
                    <MenuItem value="SANDBOX">Sandbox</MenuItem>
                    <MenuItem value="PRODUCTION">Production</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="plan"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Plan" select fullWidth helperText="Drives entitlement: STARTER=M1, PROVIDER=M1+M2, NETWORK=M1+M2+M3, ENTERPRISE=all + bulk APIs.">
                    <MenuItem value="STARTER">Starter (M1)</MenuItem>
                    <MenuItem value="PROVIDER">Provider (M1+M2)</MenuItem>
                    <MenuItem value="NETWORK">Network (M1+M2+M3)</MenuItem>
                    <MenuItem value="ENTERPRISE">Enterprise (all + bulk)</MenuItem>
                  </TextField>
                )}
              />
            </Box>
            {watchedParent && (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
                Inherits L2 client credentials from parent tenant <code>{watchedParent.slice(0, 8)}…</code>. The parent (consumer org) authenticates once; this child is acted on via X-Tenant-Id.
              </Alert>
            )}
            <Controller
              name="organizationType"
              control={control}
              render={({ field }) => (
                <TenantTypeSelect
                  value={field.value}
                  onChange={field.onChange}
                  helperText="Sourced from the platform's tenant_type_master table."
                />
              )}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Controller name="contactPerson" control={control} render={({ field }) => <TextField {...field} label="Contact person" fullWidth />} />
              <Controller name="contactPhone"  control={control} render={({ field }) => <TextField {...field} label="Phone" fullWidth />} />
            </Box>
            <Controller
              name="contactEmail"
              control={control}
              render={({ field }) => <TextField {...field} type="email" label="Contact email" fullWidth />}
            />
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Address"
                  fullWidth
                  multiline
                  minRows={2}
                  helperText="Stored on the Tenant doc as contactAddress."
                />
              )}
            />
            <Controller
              name="callbackUrl"
              control={control}
              render={({ field }) => <TextField {...field} label="Callback URL" fullWidth helperText="Where ABDM (and our internal services) post tenant-bound events." />}
            />
            {/* Tenant-admin login credentials. Stored on the Tenant doc per
                the legacy schema that gj-clinic's /api/auth/login reads from. */}
            <Box
              sx={{
                p: 2,
                borderRadius: theme.radius.sm + 'px',
                border: `1px solid ${theme.palette.border.subtle}`,
                bgcolor: theme.palette.surface.sunken,
              }}
            >
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1.5 }}>
                Tenant admin login
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Controller
                    name="username"
                    control={control}
                    rules={isEdit ? {} : { required: 'username is required' }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Username"
                        required={!isEdit}
                        fullWidth
                        autoComplete="off"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message ?? 'used for /api/auth/login'}
                      />
                    )}
                  />
                  <Controller
                    name="password"
                    control={control}
                    rules={isEdit ? {} : { required: 'password is required', minLength: { value: 4, message: 'minimum 4 characters' } }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        required={!isEdit}
                        fullWidth
                        autoComplete="new-password"
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ??
                          (isEdit ? 'leave blank to keep the existing password' : 'tenant admin will use this to sign in')
                        }
                      />
                    )}
                  />
                </Box>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Role" select fullWidth helperText="default for new tenant logins">
                      <MenuItem value="TENANT_ADMIN">TENANT_ADMIN</MenuItem>
                      <MenuItem value="CLINIC_USER">CLINIC_USER</MenuItem>
                      <MenuItem value="PLATFORM_ADMIN">PLATFORM_ADMIN</MenuItem>
                    </TextField>
                  )}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <Controller
                    name="userFullName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Full name"
                        fullWidth
                        helperText="shown in the header for this user"
                      />
                    )}
                  />
                  <Controller
                    name="userEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="email"
                        label="Email"
                        fullWidth
                        helperText="for password resets, separate from login"
                      />
                    )}
                  />
                  <Controller
                    name="userMobile"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Mobile"
                        fullWidth
                        helperText="OTP recovery, optional"
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Box>

            {/* ABDM identity. ABDM issues HFR / HIP / HIU ids together
                and most facilities get the same value for all three —
                so by default we collect a single id and mirror it into
                every field. Operators with split ids can flip the
                "Different ids" switch and edit each one separately. */}
            <AbdmIdentitySection control={control} setValue={setValue} />

            <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
              ABDM <code>client_id</code> / <code>client_secret</code> are managed at the platform level — one shared pair for every tenant.
              Set them under <strong>Platform → ABDM Bridge</strong>.
            </Alert>

            {/* BRD §4 — Integration mode picker: PLATFORM / ENTERPRISE / SDK. */}
            <Box
              sx={{
                p: 2,
                borderRadius: theme.radius.sm + 'px',
                border: `1px solid ${theme.palette.border.subtle}`,
                bgcolor: theme.palette.surface.sunken,
              }}
            >
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>
                Integration mode
              </Typography>
              <Controller
                name="integrationMode"
                control={control}
                render={({ field }) => (
                  <Stack spacing={1.5}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={field.value}
                      onChange={(_, v: TenantForm['integrationMode'] | null) => v && field.onChange(v)}
                      sx={{ flexWrap: 'wrap', gap: 1 }}
                    >
                      <ToggleButton value="PLATFORM" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Platform
                      </ToggleButton>
                      <ToggleButton value="ENTERPRISE" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Enterprise
                      </ToggleButton>
                      <ToggleButton value="SDK" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        SDK
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.tertiary">
                      {field.value === 'ENTERPRISE' &&
                        'Tenant brings its own ABDM-issued client_id / client_secret on the linked credential provider. Use for clients who registered their own bridge with NHA.'}
                      {field.value === 'SDK' &&
                        'Tenant is a HIMS vendor consuming the platform via developer keys. Same ABDM auth path as Platform mode; differs in API rate limits + scope.'}
                      {field.value === 'PLATFORM' &&
                        'Default. The platform\'s ABDM bridge handles gateway authentication; the tenant only needs HFR / HPR / HIP-ID identity.'}
                    </Typography>
                  </Stack>
                )}
              />
            </Box>

            {/* ENTERPRISE-mode credential paste panel — only when the
                operator picked Enterprise. Calls a separate PUT endpoint
                so probing / saving creds can happen without re-saving the
                whole tenant doc. */}
            <EnterpriseCredentialsPanel
              integrationMode={watch('integrationMode')}
              tenantId={initial?.tenantId}
            />

            {/* SDK-mode developer panel — visible only when mode=SDK.
                Shows client_id (copy), webhook URL, OpenAPI/Postman download,
                quick-start cURL. */}
            <SdkDeveloperPanel
              integrationMode={watch('integrationMode')}
              tenantId={initial?.tenantId}
              clientId={watch('clientId')}
              callbackUrl={watch('callbackUrl')}
            />

            {/* Platform-issued tenant credentials (NOT ABDM credentials).
                Auto-generated on create; rotate via the Regenerate button. */}
            <Box
              sx={{
                p: 2,
                borderRadius: theme.radius.sm + 'px',
                border: `1px solid ${theme.palette.border.subtle}`,
                bgcolor: theme.palette.surface.sunken,
              }}
            >
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1.5 }}>
                Platform credentials (auto-generated)
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Controller
                    name="clientId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Client ID"
                        fullWidth
                        InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
                        helperText={isEdit
                          ? 'Generated by the platform — read-only. Use Regenerate to rotate.'
                          : 'Generated by the platform on create.'}
                      />
                    )}
                  />
                  <Controller
                    name="clientSecret"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Client Secret"
                        fullWidth
                        InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
                        helperText={isEdit
                          ? 'Visible only at creation / regeneration time. Treat as a write-only secret.'
                          : 'Generated by the platform on create. Will be shown once after save.'}
                      />
                    )}
                  />
                </Box>
                {isEdit && initial && (
                  <RegenerateCredentialsButton
                    tenantId={initial.tenantId}
                    onRotated={(creds) => {
                      setValue('clientId', creds.clientId);
                      setValue('clientSecret', creds.clientSecret);
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Controller
              name="abhaEnabled"
              control={control}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>ABHA integration</Typography>
                    <Typography variant="caption" color="text.tertiary">When off, the tenant is provisioned but ABHA-related M1/M2/M3 calls will reject.</Typography>
                  </Box>
                </Stack>
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Status" select fullWidth>
                  {STATUS_CHOICES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              )}
            />
            <Alert severity="info" sx={{ borderRadius: theme.radius.sm + 'px' }}>
              ABDM client_id / client_secret aren't set here. Provision them via the credential-provider surface (separate concern); the tenant inherits the right credentials at request time.
            </Alert>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onBack ? (
          <Button onClick={onBack} disabled={isSubmitting} startIcon={<ArrowBackIcon fontSize="small" />}>
            Back
          </Button>
        ) : (
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        )}
        <Button
          type="submit"
          form="tenant-form"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create tenant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -------------------- Read-only view dialog --------------------

function TenantViewDialog({
  tenant,
  onClose,
  onEdit,
  onApiAccess,
}: {
  tenant: Tenant | null;
  onClose: () => void;
  onEdit: () => void;
  onApiAccess: () => void;
}) {
  const theme = useTheme();
  return (
    <Dialog
      open={!!tenant}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {tenant?.organizationName ?? 'Tenant'}
        <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', fontFamily: 'JetBrains Mono, monospace' }}>
          {tenant?.tenantId}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {tenant && (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>Profile</Typography>
              <FieldRow label="Status" value={<StatusPill tone={statusTone(tenant.status)}>{tenant.status ?? '—'}</StatusPill>} />
              <FieldRow label="Type" value={tenant.organizationType ?? '—'} />
              <FieldRow label="HFR id" value={tenant.hfrId ?? '—'} mono />
              <FieldRow label="ABHA enabled" value={tenant.abhaEnabled ? 'Yes' : 'No'} />
              <FieldRow label="Callback URL" value={tenant.callbackUrl ?? '—'} mono />
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>Contact</Typography>
              <FieldRow label="Person" value={tenant.contactPerson ?? '—'} />
              <FieldRow label="Email" value={tenant.contactEmail ?? '—'} />
              <FieldRow label="Phone" value={tenant.contactPhone ?? '—'} />
              <FieldRow label="Address" value={tenant.contactAddress ?? '—'} />
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>ABDM identity</Typography>
              {(() => {
                const mode = tenant.integrationMode
                  ?? (tenant.directBridge ? 'ENTERPRISE' : 'PLATFORM');
                const tone: 'success' | 'warning' | 'info' =
                  mode === 'ENTERPRISE' ? 'warning' : mode === 'SDK' ? 'info' : 'success';
                const label =
                  mode === 'ENTERPRISE' ? 'Enterprise — own ABDM creds'
                  : mode === 'SDK'      ? 'SDK — vendor / developer keys'
                  :                       'Platform — rides shared bridge';
                return (
                  <FieldRow
                    label="Integration mode"
                    value={<StatusPill tone={tone}>{label}</StatusPill>}
                  />
                );
              })()}
              <FieldRow label="HIP ID" value={tenant.abdmHIPID ?? '—'} mono />
              <FieldRow label="X-CM-ID" value={tenant.abdmXCMID ?? '—'} mono />
              <FieldRow label="HIU ID" value={tenant.abdmXHIUID ?? '—'} mono />
              {(() => {
                const mode = tenant.integrationMode
                  ?? (tenant.directBridge ? 'ENTERPRISE' : 'PLATFORM');
                if (mode === 'ENTERPRISE') {
                  return (
                    <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', mt: 1 }}>
                      This tenant authenticates to ABDM with its own <code>client_id / client_secret</code> via the linked credential provider.
                    </Typography>
                  );
                }
                if (mode === 'SDK') {
                  return (
                    <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', mt: 1 }}>
                      Vendor / developer integration. ABDM auth uses the platform bridge; the tenant's own L2 keys are scoped to API access.
                    </Typography>
                  );
                }
                return (
                  <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', mt: 1 }}>
                    ABDM <code>client_id / client_secret</code> are managed at the platform level (single shared pair) under Platform → ABDM Bridge.
                  </Typography>
                );
              })()}
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>Platform credentials</Typography>
              <FieldRow label="Client ID" value={tenant.clientId ?? '—'} mono />
              <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', mt: 0.5 }}>
                Client secret is stored as a hash and is only visible at creation / regeneration.
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>Timestamps</Typography>
              <FieldRow label="Created" value={tenant.createdAt ? utcStamp(tenant.createdAt) : '—'} />
              <FieldRow label="Updated" value={tenant.updatedAt ? utcStamp(tenant.updatedAt) : '—'} />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={onApiAccess} startIcon={<BoltIcon sx={{ fontSize: 18 }} />}>API access</Button>
        <Button
          onClick={onEdit}
          variant="contained"
          startIcon={<EditIcon sx={{ fontSize: 18 }} />}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          Edit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function blankForm(): TenantForm {
  return {
    tenantId: '',
    organizationName: '',
    organizationType: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    hfrId: '',
    callbackUrl: '',
    abhaEnabled: true,
    status: 'Pending',
    username: '',
    password: '',
    role: 'TENANT_ADMIN',
    userFullName: '',
    userEmail: '',
    userMobile: '',
    abdmHIPID: '',
    abdmXCMID: '',
    abdmXHIUID: '',
    clientId: '',
    clientSecret: '',
    // FRD additions
    parentTenantId: '',
    gstin: '',
    environment: 'SANDBOX',
    plan: 'STARTER',
    integrationMode: 'PLATFORM',
  };
}

function formFromTenant(t: Tenant | null): TenantForm {
  if (!t) return blankForm();
  return {
    tenantId: t.tenantId,
    organizationName: t.organizationName ?? '',
    organizationType: t.organizationType ?? '',
    contactPerson: t.contactPerson ?? '',
    contactEmail: t.contactEmail ?? '',
    contactPhone: t.contactPhone ?? '',
    hfrId: t.hfrId ?? '',
    callbackUrl: t.callbackUrl ?? '',
    abhaEnabled: t.abhaEnabled ?? true,
    status: t.status ?? 'Pending',
    username: t.username ?? '',
    // Never pre-fill password — write-only on the form so blank = keep.
    password: '',
    role: t.role ?? 'TENANT_ADMIN',
    userFullName: t.userFullName ?? '',
    userEmail: t.userEmail ?? '',
    userMobile: t.userMobile ?? '',
    address: t.contactAddress ?? '',
    abdmHIPID: t.abdmHIPID ?? '',
    abdmXCMID: t.abdmXCMID ?? '',
    abdmXHIUID: t.abdmXHIUID ?? '',
    // Server-issued — operator never types these. Secret is hash-only on
    // reads, so leave blank (the field is read-only and shows "—" effectively).
    clientId: t.clientId ?? '',
    clientSecret: '',
    // FRD additions
    parentTenantId: t.parentTenantId ?? '',
    gstin: t.gstin ?? '',
    environment: (t.environment === 'PRODUCTION' ? 'PRODUCTION' : 'SANDBOX'),
    plan: (
      t.plan === 'PROVIDER' || t.plan === 'NETWORK' || t.plan === 'ENTERPRISE'
        ? t.plan
        : 'STARTER'
    ),
    // Prefer the new field; fall back to the legacy boolean for un-migrated docs.
    integrationMode: (
      t.integrationMode === 'ENTERPRISE' || t.integrationMode === 'SDK' || t.integrationMode === 'PLATFORM'
        ? t.integrationMode
        : t.directBridge ? 'ENTERPRISE' : 'PLATFORM'
    ),
  };
}

/**
 * Calls the backend rotation endpoint to mint a fresh clientId/clientSecret
 * pair. The plaintext secret is the operator's ONE chance to copy it — so
 * after a successful rotation we open a reveal dialog with copy buttons
 * and a clear "rotate this at the client" warning. Closing the dialog
 * also pushes the new values into the form via the parent's onRotated.
 */
function RegenerateCredentialsButton({
  tenantId,
  onRotated,
}: {
  tenantId: string;
  onRotated: (creds: { clientId: string; clientSecret: string }) => void;
}) {
  const toast = useToast();
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [revealed, setRevealed] = useState<{ clientId: string; clientSecret: string } | null>(null);

  const rotate = async () => {
    setRunning(true);
    setConfirming(false);
    try {
      const r = await request<{ clientId: string; clientSecret: string }>({
        url: `/admin/tenants/${encodeURIComponent(tenantId)}/regenerate-credentials`,
        method: 'POST',
      });
      setRevealed(r);
      onRotated(r);
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Could not rotate credentials', detail?.message ?? detail?.error ?? (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setConfirming(true)}
        disabled={running}
        variant="outlined"
        size="small"
        sx={{ alignSelf: 'flex-start' }}
      >
        {running ? 'Rotating…' : 'Regenerate client credentials'}
      </Button>

      <ConfirmRotateDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={rotate}
      />

      <RevealCredentialsDialog
        creds={revealed}
        onClose={() => setRevealed(null)}
      />
    </>
  );
}

/**
 * Pre-rotation confirm — credentials rotation is destructive (it
 * invalidates the previous secret), so we double-check before firing
 * the mutation.
 */
function ConfirmRotateDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Rotate client credentials?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          A new <code>clientId</code> / <code>clientSecret</code> pair will be issued. The current pair will stop working immediately.
        </Typography>
        <Typography variant="caption" color="text.tertiary">
          You'll see the new secret <strong>once</strong> on the next screen. Copy it before closing.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => { onClose(); onConfirm(); }}
          variant="contained"
          color="warning"
        >
          Rotate now
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Post-rotation reveal — shows the plaintext clientId / clientSecret
 * with copy buttons. The dialog blocks closing on backdrop click so
 * the operator can't accidentally dismiss it before copying. After
 * close the secret is gone from the UI forever.
 */
function RevealCredentialsDialog({
  creds,
  onClose,
}: {
  creds: { clientId: string; clientSecret: string } | null;
  onClose: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (creds) setAcknowledged(false);
  }, [creds]);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed', 'Select the text manually and copy.');
    }
  };

  return (
    <Dialog
      open={!!creds}
      // Block backdrop / Escape so the secret can't be accidentally dismissed.
      onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>New client credentials</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
            This is the <strong>only time</strong> the secret will be shown. Copy it now and rotate it
            in every client integration that authenticates as this tenant — once you close this dialog
            the plaintext is gone.
          </Alert>

          <Stack spacing={0.5}>
            <Typography variant="overline" color="text.tertiary">Client ID</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={creds?.clientId ?? ''}
                fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => creds && copy('Client ID', creds.clientId)}
              >
                Copy
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="overline" color="text.tertiary">Client Secret</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={creds?.clientSecret ?? ''}
                fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => creds && copy('Client Secret', creds.clientSecret)}
              >
                Copy
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Switch checked={acknowledged} onChange={(_, v) => setAcknowledged(v)} size="small" />
            <Typography variant="body2">
              I've copied the secret and will rotate it at the client.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={!acknowledged}
          variant="contained"
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Picker for parent tenants — top-level tenants only (i.e. the consumer
 * orgs: HMIS vendors, chain HQs). Used when adding a child tenant under
 * an existing consumer org per FRD §8.0.7 V1/V2 onboarding variants.
 *
 * "Top-level" = `parentTenantId` is null/blank. We filter client-side
 * since the tenant list is small enough not to need a server filter.
 */
function ParentTenantPicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const { data } = useSWR('/admin/tenants', () => tenants.list(), { dedupingInterval: 30_000 });
  const topLevel = useMemo(
    () => (data ?? []).filter((t) => !t.parentTenantId),
    [data],
  );
  const selected = useMemo(
    () => topLevel.find((t) => t.tenantId === value) ?? null,
    [topLevel, value],
  );

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={topLevel}
      value={selected}
      onChange={(_, next) => onChange(next?.tenantId ?? '')}
      getOptionLabel={(o) => o.organizationName ?? o.tenantId}
      isOptionEqualToValue={(a, b) => a.tenantId === b.tenantId}
      noOptionsText="No top-level tenants — create the consumer org (HQ / vendor) first."
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.tenantId} sx={{ py: 0.75 }}>
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
              {o.organizationName ?? '—'}
            </Typography>
            <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {o.organizationType ?? '—'} · {redactMiddle(o.tenantId, 8, 8)}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Parent tenant (consumer org)"
          required
          error={!!error}
          helperText={error ?? 'Pick the chain HQ / vendor under which this tenant operates.'}
        />
      )}
    />
  );
}

/**
 * ABDM identity block. Most facilities receive a single id from ABDM
 * that doubles as HFR-ID / HIP-ID / HIU-ID, so the default UI is one
 * input that mirrors into all three. The "Different ids" switch
 * unfolds the original three-field grid for the rare cases where the
 * facility has split values.
 *
 * The mirror is one-way: the master input writes to all three fields
 * on every keystroke. When the operator unfolds and edits one field
 * individually, that field stops mirroring (the master input no
 * longer matches it, so the three fields are now in custom mode).
 */
/**
 * BRD §4 ENTERPRISE-mode credential intake. Visible only when the
 * tenant's integrationMode === 'ENTERPRISE'. Operator pastes the
 * tenant's own ABDM-issued client_id / client_secret + HIP / CM / HIU
 * ids; we PUT them to /admin/tenants/{id}/abdm-credentials which
 * upserts the linked credential_provider row.
 *
 * The panel also exposes a "Test connectivity" button that calls
 * /probe and renders the resolved mode + duration + PASS/FAIL inline,
 * so the operator can verify creds end-to-end before flipping the
 * tenant live.
 */
function EnterpriseCredentialsPanel({
  integrationMode,
  tenantId,
}: {
  integrationMode: 'PLATFORM' | 'ENTERPRISE' | 'SDK' | undefined;
  tenantId: string | undefined;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [hipId, setHipId] = useState('');
  const [cmId, setCmId] = useState('');
  const [hiuId, setHiuId] = useState('');
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [lastProbe, setLastProbe] = useState<{
    ok: boolean;
    durationMs?: number;
    message?: string;
    integrationMode?: string;
  } | null>(null);

  if (integrationMode !== 'ENTERPRISE') return null;

  const canSave = clientId.trim().length > 0 && hipId.trim().length > 0;

  const onSaveCreds = async () => {
    if (!tenantId) {
      toast.error('Save the tenant first', 'Create the tenant before pasting its ABDM credentials.');
      return;
    }
    setSaving(true);
    try {
      await tenants.upsertAbdmCredentials(tenantId, {
        clientId: clientId.trim(),
        ...(clientSecret ? { clientSecret } : {}),
        abdmHIPID: hipId.trim(),
        abdmXCMID: cmId.trim() || undefined,
        abdmXHIUID: hiuId.trim() || undefined,
      });
      toast.success('ABDM credentials saved', 'Linked credential provider updated.');
      setClientSecret(''); // write-only, clear on save
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Save failed', detail?.message ?? detail?.error ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onProbe = async () => {
    if (!tenantId) {
      toast.error('Save the tenant first', 'Create the tenant before probing connectivity.');
      return;
    }
    setProbing(true);
    setLastProbe(null);
    try {
      const r = await tenants.probe(tenantId);
      setLastProbe({
        ok: r.ok,
        durationMs: r.durationMs,
        message: r.message ?? r.error,
        integrationMode: r.integrationMode,
      });
      if (r.ok) toast.success('ABDM connectivity OK', `${r.durationMs ?? 0}ms · ${r.integrationMode}`);
      else toast.error('ABDM connectivity FAILED', r.message ?? r.error ?? 'unknown');
    } catch (e) {
      setLastProbe({ ok: false, message: (e as Error).message });
      toast.error('Probe failed', (e as Error).message);
    } finally {
      setProbing(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: theme.radius.sm + 'px',
        border: `1px solid ${theme.palette.status.warning}55`,
        bgcolor: theme.palette.status.warningBg,
      }}
    >
      <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1.5 }}>
        Enterprise — tenant's own ABDM credentials
      </Typography>
      <Stack spacing={2}>
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
          Paste the <code>client_id</code> / <code>client_secret</code> NHA issued to this tenant
          along with their HIP / CM / HIU ids. These overwrite the linked credential provider.
          The secret is write-only — leave blank on edit to keep the existing value.
        </Alert>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            fullWidth
            required
            autoComplete="off"
            InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } }}
          />
          <TextField
            label="Client Secret"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            type="password"
            fullWidth
            autoComplete="new-password"
            helperText={tenantId ? 'Leave blank to keep existing' : ' '}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          <TextField
            label="HIP ID"
            value={hipId}
            onChange={(e) => setHipId(e.target.value)}
            fullWidth
            required
            helperText="ABDM HIP-ID for this tenant"
          />
          <TextField
            label="X-CM-ID"
            value={cmId}
            onChange={(e) => setCmId(e.target.value)}
            fullWidth
            helperText="consent manager id"
          />
          <TextField
            label="HIU ID"
            value={hiuId}
            onChange={(e) => setHiuId(e.target.value)}
            fullWidth
            helperText="HIU id (often = HIP)"
          />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            onClick={onSaveCreds}
            disabled={!canSave || saving || !tenantId}
            sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
          >
            {saving ? 'Saving…' : 'Save ABDM credentials'}
          </Button>
          <Button
            variant="outlined"
            onClick={onProbe}
            disabled={probing || !tenantId}
          >
            {probing ? 'Probing…' : 'Test connectivity'}
          </Button>
          {lastProbe && (
            <StatusPill tone={lastProbe.ok ? 'success' : 'danger'}>
              {lastProbe.ok
                ? `OK · ${lastProbe.durationMs ?? 0}ms · ${lastProbe.integrationMode}`
                : `FAIL · ${lastProbe.message ?? 'unknown'}`}
            </StatusPill>
          )}
        </Stack>
        {!tenantId && (
          <Typography variant="caption" color="text.tertiary">
            Save the tenant first; then return here to paste credentials and probe.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * BRD §4.3 SDK-mode developer panel. Visible only when
 * {@code integrationMode === 'SDK'}. The L2 client_id this tenant uses
 * to authenticate to OUR API is the heart of the panel — operator copies
 * it and hands it to the HIMS vendor along with the OpenAPI / Postman
 * artifacts. The vendor never sees ABDM credentials at all (Aggregator
 * model — we own L1).
 *
 * The download buttons stream classpath artifacts via fetch + Blob so
 * the JWT in localStorage rides along on the request (axios interceptor).
 * Direct anchor download wouldn't carry the bearer header.
 */
function SdkDeveloperPanel({
  integrationMode,
  tenantId,
  clientId,
  callbackUrl,
}: {
  integrationMode: 'PLATFORM' | 'ENTERPRISE' | 'SDK' | undefined;
  tenantId: string | undefined;
  clientId: string | undefined;
  callbackUrl: string | undefined;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [downloading, setDownloading] = useState<string | null>(null);

  if (integrationMode !== 'SDK') return null;

  const baseUrl = (import.meta.env['VITE_API_BASE_URL'] as string | undefined)?.replace(/\/$/, '') ?? '';

  const copy = async (label: string, value: string | undefined) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed', 'Select the text manually and copy.');
    }
  };

  const downloadArtifact = async (path: string, filename: string) => {
    setDownloading(filename);
    try {
      const blob = await request<Blob>({
        url: path,
        method: 'GET',
        responseType: 'blob',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error('Download failed', (e as Error).message);
    } finally {
      setDownloading(null);
    }
  };

  // OAuth2 client-credentials grant — preferred for server-to-server SDK use.
  // Vendors authenticate with their L2 client_id/client_secret directly,
  // no human user needed. Returns an access_token (~30 min TTL).
  const cidShown = clientId || '<client_id from above>';
  const tidShown = tenantId ?? '<your-tenant-id>';
  const quickStart = `# 1) Get an access token via OAuth2 client_credentials
curl -X POST ${baseUrl}/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{"grant_type":"client_credentials","client_id":"${cidShown}","client_secret":"YOUR_CLIENT_SECRET"}'

# returns { "access_token":"eyJ...", "expires_in":1800, "tenantId":"${tidShown}", ... }
JWT=$(echo "$RESPONSE" | jq -r .access_token)

# 2) Call any /api/abdm/* endpoint with the JWT + your tenant id
curl -X POST ${baseUrl}/api/abdm/v3/request/otp \\
  -H "Authorization: Bearer $JWT" \\
  -H "X-Tenant-Id: ${tidShown}" \\
  -H "Content-Type: application/json" \\
  -d '{"tenantId":"${tidShown}","type":"aadhaar","value":"123456789012","txnId":""}'

# Token expired? Just re-issue with the same client_credentials call.`;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: theme.radius.sm + 'px',
        border: `1px solid ${theme.palette.status.info}55`,
        bgcolor: theme.palette.status.infoBg,
      }}
    >
      <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1.5 }}>
        SDK developer kit
      </Typography>
      <Stack spacing={2}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
          Hand the <code>client_id</code> below to the HIMS vendor along with the OpenAPI / Postman
          artifacts. Vendors authenticate with <code>username</code> / <code>password</code> issued
          via the Users page; they never see ABDM credentials directly.
        </Alert>

        {/* Client ID (read-only) + copy */}
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.tertiary">L2 Client ID (vendor uses this)</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={clientId ?? ''}
              fullWidth
              InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } }}
            />
            <Button size="small" variant="outlined" onClick={() => copy('Client ID', clientId)} disabled={!clientId}>
              Copy
            </Button>
          </Stack>
          <Typography variant="caption" color="text.tertiary">
            Rotate via the <strong>Regenerate client credentials</strong> button (Platform credentials section above).
          </Typography>
        </Stack>

        {/* Webhook callback URL surface */}
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.tertiary">Webhook callback URL</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={callbackUrl ?? ''}
              fullWidth
              placeholder="https://vendor.example.com/webhooks/abdm"
              InputProps={{ readOnly: true, sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } }}
            />
            <Button size="small" variant="outlined" onClick={() => copy('Callback URL', callbackUrl)} disabled={!callbackUrl}>
              Copy
            </Button>
          </Stack>
          <Typography variant="caption" color="text.tertiary">
            ABDM consent / data-fetch callbacks land here. Edit it in the <strong>Callback URL</strong> field above.
          </Typography>
        </Stack>

        {/* Artifact downloads */}
        <Stack spacing={1}>
          <Typography variant="overline" color="text.tertiary">Developer artifacts</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              onClick={() => downloadArtifact('/admin/sdk/postman.json', 'ayushsetu-abdm-sdk.postman_collection.json')}
              disabled={downloading !== null}
            >
              {downloading === 'ayushsetu-abdm-sdk.postman_collection.json' ? 'Downloading…' : 'Download Postman collection'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={`${baseUrl || ''}/v3/api-docs`}
              target="_blank"
              rel="noreferrer"
            >
              OpenAPI spec (v3/api-docs)
            </Button>
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={`${baseUrl || ''}/swagger-ui.html`}
              target="_blank"
              rel="noreferrer"
            >
              Live Swagger UI
            </Button>
          </Stack>
        </Stack>

        {/* Quick-start cURL */}
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="overline" color="text.tertiary">Quick start (cURL)</Typography>
            <Button size="small" onClick={() => copy('Quick start', quickStart)}>Copy</Button>
          </Stack>
          <Box
            component="pre"
            sx={{
              p: 1.5,
              borderRadius: theme.radius.sm + 'px',
              border: `1px solid ${theme.palette.border.subtle}`,
              bgcolor: theme.palette.surface.sunken,
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
            }}
          >
            {quickStart}
          </Box>
        </Stack>

        {!tenantId && (
          <Typography variant="caption" color="text.tertiary">
            Save the tenant first to populate the L2 client_id and tenantId in the snippets above.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function AbdmIdentitySection({
  control,
  setValue,
}: {
  control: Control<TenantForm>;
  setValue: UseFormSetValue<TenantForm>;
}) {
  const theme = useTheme();
  const hfrId = useWatch({ control, name: 'hfrId' });
  const hipId = useWatch({ control, name: 'abdmHIPID' });
  const cmId = useWatch({ control, name: 'abdmXCMID' });
  const hiuId = useWatch({ control, name: 'abdmXHIUID' });

  // Same-id detection: all populated values match (treat blanks as "match"
  // so a fresh form starts in single-input mode).
  const allMatch = useMemo(() => {
    const vals = [hfrId, hipId, cmId, hiuId].filter((v) => (v ?? '').trim().length > 0);
    if (vals.length === 0) return true;
    return vals.every((v) => v === vals[0]);
  }, [hfrId, hipId, cmId, hiuId]);

  const [splitMode, setSplitMode] = useState(!allMatch);

  // If the data we hydrate later has split ids, surface the split UI.
  useEffect(() => {
    if (!allMatch && !splitMode) setSplitMode(true);
  }, [allMatch, splitMode]);

  const masterValue = hfrId || hipId || cmId || hiuId || '';

  const setMaster = (v: string) => {
    const next = v.trim();
    setValue('hfrId', next, { shouldDirty: true });
    setValue('abdmHIPID', next, { shouldDirty: true });
    setValue('abdmXCMID', next, { shouldDirty: true });
    setValue('abdmXHIUID', next, { shouldDirty: true });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: theme.radius.sm + 'px',
        border: `1px solid ${theme.palette.border.subtle}`,
        bgcolor: theme.palette.surface.sunken,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="overline" color="text.tertiary">ABDM identity</Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.tertiary">Different ids</Typography>
          <Switch
            size="small"
            checked={splitMode}
            onChange={(_, v) => setSplitMode(v)}
          />
        </Stack>
      </Stack>

      {!splitMode ? (
        <Stack spacing={1}>
          <TextField
            label="HFR / HIP / HIU id"
            placeholder="e.g. medzioassist"
            fullWidth
            value={masterValue}
            onChange={(e) => setMaster(e.target.value)}
            helperText="ABDM issues a single id that doubles as HFR / HIP / HIU for most facilities. Toggle 'Different ids' if your facility has split values."
          />
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Controller
            name="hfrId"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="HFR ID" fullWidth helperText="ABDM Health Facility Registry id." />
            )}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
            <Controller
              name="abdmHIPID"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="HIP ID" fullWidth helperText="health information provider" />
              )}
            />
            <Controller
              name="abdmXCMID"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="X-CM-ID" fullWidth helperText="consent manager" />
              )}
            />
            <Controller
              name="abdmXHIUID"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="HIU ID" fullWidth helperText="data-pull flows" />
              )}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
